import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

import PropTypes from "prop-types";
import AppShellSkeleton from "src/components/AppShellSkeleton/AppShellSkeleton";
import AuthLayout from "src/components/AuthLayout";
import SelectTenantPrompt from "src/components/SelectTenantPrompt";
import { useAuth0 } from "src/contexts/auth0Provider";
import { PUBLIC_URLS } from "src/consts/urlPaths";
import {
  auth0UserMetadataKey,
  hasTenantPrompt,
  isInternalSupportUser,
  isOnboardingUser,
} from "src/services/auth";
import { isEnvTrue } from "src/services/env";
import { getActiveTenant, saveActiveTenant } from "src/services/localStorage";
import { getTenantFromUrl } from "src/services/tenantUrl";

import AuthenticatedApp from "./AuthenticatedApp";
import { UnauthenticatedApp } from "./UnauthenticatedApp";
import PublicRoutes from "./PublicRoutes";

const App = ({ themeSwitcher }) => {
  const {
    auth0ErrorContext,
    isAccessDenied,
    isGenericDomain,
    getTokenSilently,
    isAuthenticated,
    loading,
    loginWithRedirect,
    user,
  } = useAuth0();
  const isPublicUrl = PUBLIC_URLS.includes(window.location.pathname);

  // Track the last processed URL tenant to avoid re-processing on every render
  const lastProcessedUrlTenantRef = useRef(null);

  // State to trigger re-render after URL tenant is processed
  const [urlTenantProcessed, setUrlTenantProcessed] = useState(false);

  // State to track URL changes (for back/forward button navigation)
  const [urlChangeCounter, setUrlChangeCounter] = useState(0);

  // Listen for URL changes via back/forward button
  useEffect(() => {
    const handlePopState = () => {
      setUrlChangeCounter((prev) => prev + 1);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // Handle tenant from URL for internal support users (layout effect avoids an extra paint)
  useLayoutEffect(() => {
    if (!isAuthenticated || !user) {
      return;
    }

    const urlTenantId = getTenantFromUrl();

    // If no URL tenant, mark as processed (nothing to do)
    if (!urlTenantId) {
      setUrlTenantProcessed(true);
      lastProcessedUrlTenantRef.current = null;
      return;
    }

    // Skip if already processed this tenant
    if (lastProcessedUrlTenantRef.current === urlTenantId) {
      return;
    }

    // Only process for internal support users
    if (!isInternalSupportUser(user)) {
      setUrlTenantProcessed(true);
      return;
    }

    lastProcessedUrlTenantRef.current = urlTenantId;

    const tenantList = user[auth0UserMetadataKey]?.tenantList?.tenants || [];
    const matchedTenant = tenantList.find((t) => t.tenantId === urlTenantId);

    if (matchedTenant) {
      const tenantWithLabel = {
        ...matchedTenant,
        label: matchedTenant.name
          ? `${matchedTenant.name} (${matchedTenant.tenantId})`
          : `${matchedTenant.tenantId}`,
      };
      saveActiveTenant(user?.sub || "", tenantWithLabel);
    } else if (
      urlTenantId === "localhost" &&
      isEnvTrue(process.env.REACT_APP_IS_OFFLINE)
    ) {
      // Localhost in offline mode - always save localhost tenant (idempotent)
      saveActiveTenant(user?.sub || "", {
        label: "Local Host",
        tenantUrl: process.env.REACT_APP_GRAPHQL_URL,
        tenantId: "localhost",
      });
    } else {
      // Invalid tenant in URL - clear active tenant to force tenant selector
      saveActiveTenant(user?.sub || "", null);
    }

    // Trigger re-render so render logic picks up newly saved tenant
    setUrlTenantProcessed(true);
  }, [isAuthenticated, user, urlChangeCounter]);

  useEffect(() => {
    if (loading || isAuthenticated) {
      return;
    }

    if (!isAccessDenied && !isPublicUrl) {
      const fn = async () => {
        // if the url is not a public URL, always redirect to
        // the original requested url including query params
        await loginWithRedirect({
          appState: {
            targetUrl: window.location.pathname + window.location.search,
          },
          redirect_uri: window.location.origin,
        });
      };
      fn();
    }
  }, [
    loading,
    isAuthenticated,
    loginWithRedirect,
    isAccessDenied,
    isPublicUrl,
  ]);

  if (loading) {
    return (
      <AppShellSkeleton
        data-testid="app-auth-bootstrap-skeleton"
        aria-label="Signing in"
        statusMessage="Confirming authentication..."
      />
    );
  }

  if (isAccessDenied) {
    return <UnauthenticatedApp errorContext={auth0ErrorContext} />;
  }

  if (isPublicUrl) {
    return <PublicRoutes />;
  }

  if (isOnboardingUser(user)) {
    window.location.href = process.env.REACT_APP_TEAM_DIRECTORY_DOMAIN;
  }

  if (isAuthenticated) {
    // Wait for URL tenant to be processed before checking localStorage
    const urlTenantId = getTenantFromUrl();
    if (urlTenantId && isInternalSupportUser(user) && !urlTenantProcessed) {
      return (
        <AppShellSkeleton
          data-testid="app-auth-bootstrap-skeleton"
          aria-label="Loading tenant"
        />
      );
    }

    const savedActiveTenant = getActiveTenant(user?.sub || "");
    const isDisplayTenantListPrompt =
      hasTenantPrompt(user) && !savedActiveTenant;

    if (isDisplayTenantListPrompt) {
      return (
        <SelectTenantPrompt user={user} isFullScreen>
          <AuthenticatedApp
            user={user}
            isGenericDomain={isGenericDomain}
            getTokenSilently={getTokenSilently}
            loginWithRedirect={loginWithRedirect}
            themeSwitcher={themeSwitcher}
          />
        </SelectTenantPrompt>
      );
    }

    if (
      process.env.REACT_APP_LOCAL_BUNDLE === "true" &&
      !hasTenantPrompt(user)
    ) {
      return (
        <UnauthenticatedApp
          errorContext={{
            error: "local-dev-user-invalid",
            errorDescription:
              "If running start:bundle, you must log in with your support user",
            source: "local-dev",
          }}
        />
      );
    }

    return (
      <AuthenticatedApp
        user={user}
        isGenericDomain={isGenericDomain}
        getTokenSilently={getTokenSilently}
        loginWithRedirect={loginWithRedirect}
        themeSwitcher={themeSwitcher}
      />
    );
  }

  return <AuthLayout noPanel={true} />;
};

App.propTypes = {
  themeSwitcher: PropTypes.func,
};

export default App;
